// @flowgenix/shared-ui/utils/native/index.js
//
// Phase 2 / Step H — thin Capacitor abstraction layer.
//
// The web build is the source of truth. When the bundle runs inside
// Capacitor (iOS / Android), we want native superpowers (camera,
// filesystem, push, deep links, splash screen, status bar) WITHOUT
// teaching every page about Capacitor's native bridge.
//
// Design rules:
//   1. Every helper MUST be safe to call from a plain web page —
//      detect Capacitor at runtime and fall back to a sensible web
//      equivalent (or no-op). No top-level await on @capacitor/*.
//   2. Capacitor plugins are imported lazily and only resolved when
//      we're definitely inside a native shell (`isNative()`). The
//      dynamic-import expression uses runtime variable concatenation
//      (`'@capacitor/' + name`) so Rollup's static analysis cannot
//      walk into them at web-build time. Web builds therefore don't
//      need @capacitor/{app,camera,...} installed in their workspace
//      node_modules — only the native build does.
//   3. The exports are stable, ergonomic shapes — pages call
//      `await pickPhoto()` instead of constructing
//      `Camera.getPhoto({ ... })` themselves.
//
// Reference: CAPACITOR_IOS_WRAP.md (under core/knowledge/procedures/).

export function isNative() {
  if (typeof window === 'undefined') return false;
  return Boolean(window?.Capacitor?.isNativePlatform?.());
}

export function platform() {
  if (typeof window === 'undefined') return 'server';
  return window?.Capacitor?.getPlatform?.() || 'web';
}

// Lazy native-plugin loader. Runtime variable interpolation in the
// import specifier defeats Rollup's static analysis so web builds do
// not try to resolve @capacitor/{app,camera,...}. Native builds run
// from the JS bundle Capacitor copies into ios/App/public, where the
// plugins ARE available alongside @capacitor/core.
async function _loadCapacitorPlugin(name) {
  if (!isNative()) return null;
  // The string concatenation is the trick — Rollup needs a literal
  // string after `import(` to walk; with concatenation it bails out
  // and just emits a runtime import.
  const spec = '@capacitor/' + name;
  try {
    return await import(/* @vite-ignore */ spec);
  } catch (_err) {
    return null;
  }
}

// ── App lifecycle ─────────────────────────────────────────────────────

export async function onAppPause(handler) {
  const mod = await _loadCapacitorPlugin('app');
  if (mod && mod.App) {
    const sub = await mod.App.addListener('pause', handler);
    return () => sub.remove();
  }
  if (typeof document === 'undefined') return () => {};
  const onVis = () => {
    if (document.visibilityState === 'hidden') handler();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

export async function onAppResume(handler) {
  const mod = await _loadCapacitorPlugin('app');
  if (mod && mod.App) {
    const sub = await mod.App.addListener('resume', handler);
    return () => sub.remove();
  }
  if (typeof document === 'undefined') return () => {};
  const onVis = () => {
    if (document.visibilityState === 'visible') handler();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

export async function onDeepLink(handler) {
  const mod = await _loadCapacitorPlugin('app');
  if (!mod || !mod.App) return () => {};
  const sub = await mod.App.addListener('appUrlOpen', (data) => handler(data?.url || ''));
  return () => sub.remove();
}

// ── Status bar / splash ───────────────────────────────────────────────

export async function setStatusBarStyle({ style = 'dark' } = {}) {
  const mod = await _loadCapacitorPlugin('status-bar');
  if (!mod || !mod.StatusBar) return;
  await mod.StatusBar.setStyle({
    style: style === 'light' ? mod.Style.Light : mod.Style.Dark,
  }).catch(() => {});
}

export async function hideSplash() {
  const mod = await _loadCapacitorPlugin('splash-screen');
  if (mod && mod.SplashScreen) await mod.SplashScreen.hide().catch(() => {});
}

// ── Camera / photo picker ─────────────────────────────────────────────

/**
 * pickPhoto({ source }) — opens the OS camera or photo library and
 * returns a { dataUrl, mimeType, fileName } object suitable for an
 * upload payload. Web fallback uses an <input type="file" capture>.
 */
export async function pickPhoto({ source = 'prompt', quality = 80 } = {}) {
  if (isNative()) {
    const mod = await _loadCapacitorPlugin('camera');
    if (mod && mod.Camera) {
      const sourceMap = {
        camera: mod.CameraSource.Camera,
        library: mod.CameraSource.Photos,
        prompt: mod.CameraSource.Prompt,
      };
      const photo = await mod.Camera.getPhoto({
        quality,
        resultType: mod.CameraResultType.DataUrl,
        source: sourceMap[source] || mod.CameraSource.Prompt,
        allowEditing: false,
      }).catch(() => null);
      if (!photo) return null;
      return {
        dataUrl: photo.dataUrl,
        mimeType: `image/${photo.format || 'jpeg'}`,
        fileName: `photo.${photo.format || 'jpeg'}`,
      };
    }
  }
  // Web fallback — programmatic file input.
  return new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve(null);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (source === 'camera') input.capture = 'environment';
    input.onchange = () => {
      const f = input.files && input.files[0];
      if (!f) return resolve(null);
      const reader = new FileReader();
      reader.onload = () =>
        resolve({ dataUrl: reader.result, mimeType: f.type || 'image/jpeg', fileName: f.name });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(f);
    };
    input.click();
  });
}

// ── Filesystem ────────────────────────────────────────────────────────

/**
 * saveFile({ name, data, mimeType }) — write a Blob/string to a
 * persistent location. On native, lands in Documents/. On web, falls
 * back to a download link click (data → user's downloads folder).
 */
export async function saveFile({ name, data, mimeType = 'application/octet-stream' }) {
  if (isNative()) {
    const mod = await _loadCapacitorPlugin('filesystem');
    if (mod && mod.Filesystem) {
      const isText = typeof data === 'string';
      await mod.Filesystem.writeFile({
        path: name,
        data: isText ? data : await blobToBase64(data),
        directory: mod.Directory.Documents,
        encoding: isText ? mod.Encoding.UTF8 : undefined,
        recursive: true,
      });
      return { path: `Documents/${name}` };
    }
  }
  if (typeof document === 'undefined') return null;
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { path: name };
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result || '');
      const idx = dataUrl.indexOf(',');
      resolve(idx >= 0 ? dataUrl.slice(idx + 1) : '');
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

// ── Push notifications ────────────────────────────────────────────────

/**
 * registerPush(handler) — request permission + register for APNs/FCM,
 * then forward incoming notifications to `handler({ title, body, data })`.
 * Returns the device token on success or null. Web is a no-op (browser
 * push uses VAPID + PushManager which is a separate path).
 */
export async function registerPush(handler) {
  const mod = await _loadCapacitorPlugin('push-notifications');
  if (!mod || !mod.PushNotifications) return null;
  const { PushNotifications } = mod;

  const perm = await PushNotifications.requestPermissions().catch(() => null);
  if (!perm || perm.receive !== 'granted') return null;

  let resolveToken;
  const tokenPromise = new Promise((resolve) => {
    resolveToken = resolve;
  });

  await PushNotifications.addListener('registration', (t) => resolveToken(t.value));
  await PushNotifications.addListener('registrationError', () => resolveToken(null));
  await PushNotifications.addListener('pushNotificationReceived', (n) =>
    handler({ title: n.title, body: n.body, data: n.data || {} }),
  );
  await PushNotifications.addListener('pushNotificationActionPerformed', (a) =>
    handler({
      title: a.notification?.title,
      body: a.notification?.body,
      data: { ...(a.notification?.data || {}), action: a.actionId },
    }),
  );
  await PushNotifications.register();
  return tokenPromise;
}
